import React, { useState, useEffect, useCallback } from 'react';
import {
    collection,
    collectionGroup,
    query,
    where,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    updateDoc,
    orderBy,
    serverTimestamp,
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRoadmapPilot } from '../utils/useRoadmapPilot';   // 🗺️ T-18 고지 = 플래그 AND 파일럿 치과(서버 위탁 확인) (2026-10-06)
import { STAFF_INVITE_NOTICE } from '../services/careRoadmapApi';
import {
    Users,
    Mail,
    Copy,
    Trash2,
    UserPlus,
    Clock,
    CheckCircle,
    AlertCircle,
    UserMinus,
    RefreshCw,
    Smartphone
} from 'lucide-react';

function StaffInviteManagement({ user }) {
    const [activeTab, setActiveTab] = useState('staff'); // staff, invites
    // 🗺️ T-18 직원 초대 고지 — 치과 && 플래그 roadmapEngine(strict) AND 파일럿 치과(useRoadmapPilot, fail-closed) 일 때만 초대 폼 상단 고정 (2026-10-06, 계약_질문로드맵_웹 §4)
    const { on: roadmapOn } = useRoadmapPilot(user);
    const [staffList, setStaffList] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    const companyId = user?.uid;
    const companyName = user?.businessName || '';

    const loadData = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);

        try {
            // 직원 목록 조회
            const staffQuery = query(
                collection(db, 'users'),
                where('companyId', '==', companyId),
                where('userType', '==', 'staff')
            );
            const staffSnapshot = await getDocs(staffQuery);
            const staffData = staffSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setStaffList(staffData);

            // 초대 목록 조회
            const inviteQuery = query(
                collection(db, 'staffInvitations'),
                where('companyId', '==', companyId),
                orderBy('createdAt', 'desc')
            );
            const inviteSnapshot = await getDocs(inviteQuery);
            const inviteData = inviteSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setInvitations(inviteData);
        } catch (error) {
            console.error('데이터 로드 실패:', error);
            setMessage({ type: 'error', text: '데이터를 불러오는데 실패했습니다.' });
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // 초대 코드 생성
    const generateInviteCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let code = '';
        for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
    };

    // 초대 생성
    const createInvitation = async () => {
        try {
            const code = generateInviteCode();
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 7); // 7일 후 만료

            await addDoc(collection(db, 'staffInvitations'), {
                companyId: companyId,
                companyName: companyName,
                inviteCode: code,
                status: 'pending',
                createdAt: serverTimestamp(),
                expiresAt: Timestamp.fromDate(expiresAt),
                createdBy: user.uid
            });

            setMessage({ type: 'success', text: '초대 코드가 생성되었습니다.' });
            loadData();
        } catch (error) {
            console.error('초대 생성 실패:', error);
            setMessage({ type: 'error', text: '초대 생성에 실패했습니다.' });
        }
    };

    // 초대 삭제
    const deleteInvitation = async (invitationId) => {
        if (!window.confirm('이 초대를 삭제하시겠습니까?')) return;

        try {
            await deleteDoc(doc(db, 'staffInvitations', invitationId));
            setMessage({ type: 'success', text: '초대가 삭제되었습니다.' });
            loadData();
        } catch (error) {
            console.error('초대 삭제 실패:', error);
            setMessage({ type: 'error', text: '초대 삭제에 실패했습니다.' });
        }
    };

    // 📵 직원 세션 원격 차단 (2026-08-17 대표 확정 #32 ③) — 폰·브라우저 포함 모든 기기.
    // PC 원격 로그아웃(agentCommands)은 덴클 프로그램 전용이라 폰 로그인을 못 끊었다.
    const [revoking, setRevoking] = useState(null);
    const revokeSessions = async (staffId, staffName) => {
        if (revoking) return;
        if (!window.confirm(`${staffName}님의 로그인을 모든 기기(폰·브라우저 포함)에서 끊을까요?\n\n적용까지 최대 1시간 걸릴 수 있으며, 그 뒤 다시 로그인해야 사용할 수 있습니다.\n(직원 해제가 아니라 로그인만 끊는 것입니다 — 계정·소속은 유지됩니다)`)) return;
        setRevoking(staffId);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'revokeStaffSessions');
            await fn({ targetUid: staffId });
            setMessage({ type: 'success', text: `${staffName}님의 모든 기기 로그인을 끊었습니다 — 최대 1시간 안에 적용됩니다.` });
        } catch (e) {
            console.error('직원 세션 차단 실패:', e);
            setMessage({ type: 'error', text: `세션 차단에 실패했습니다: ${e?.message || e}` });
        } finally {
            setRevoking(null);
        }
    };

    // 직원 해제
    const removeStaff = async (staffId, staffName) => {
        if (!window.confirm(`${staffName}님을 직원에서 해제하시겠습니까?\n해제 후에는 회사 데이터에 접근할 수 없습니다.`)) return;

        try {
            await updateDoc(doc(db, 'users', staffId), {
                companyId: null,
                userType: 'individual'
            });

            // 커뮤니티 게시글의 회사 정보 초기화
            try {
                const postsQuery = query(
                    collection(db, 'communityPosts'),
                    where('authorId', '==', staffId)
                );
                const postsSnapshot = await getDocs(postsQuery);

                if (postsSnapshot.docs.length > 0) {
                    const batch = writeBatch(db);
                    postsSnapshot.docs.forEach(postDoc => {
                        batch.update(postDoc.ref, {
                            authorCompany: '',
                            authorBusinessType: ''
                        });
                    });
                    await batch.commit();
                }

                // 커뮤니티 댓글의 회사 정보 초기화
                const commentsQuery = query(
                    collectionGroup(db, 'comments'),
                    where('authorId', '==', staffId)
                );
                const commentsSnapshot = await getDocs(commentsQuery);

                if (commentsSnapshot.docs.length > 0) {
                    const commentBatch = writeBatch(db);
                    commentsSnapshot.docs.forEach(commentDoc => {
                        commentBatch.update(commentDoc.ref, {
                            authorCompany: ''
                        });
                    });
                    await commentBatch.commit();
                }
            } catch (e) {
                console.warn('커뮤니티 게시글 업데이트 실패 (무시):', e);
            }

            setMessage({ type: 'success', text: `${staffName}님이 직원에서 해제되었습니다.` });
            loadData();
        } catch (error) {
            console.error('직원 해제 실패:', error);
            setMessage({ type: 'error', text: '직원 해제에 실패했습니다.' });
        }
    };

    // 클립보드 복사
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setMessage({ type: 'success', text: '클립보드에 복사되었습니다.' });
    };

    // 날짜 포맷
    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR');
    };

    // 만료 확인
    const isExpired = (expiresAt) => {
        if (!expiresAt) return false;
        const expDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
        return expDate < new Date();
    };

    const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
    const usedInvitations = invitations.filter(inv => inv.status === 'used');

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loading}>로딩 중...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <Users size={28} color="#6366f1" />
                    직원 관리
                </h1>
                <p style={styles.subtitle}>{companyName}의 직원을 관리합니다</p>
            </div>

            {message && (
                <div style={{
                    ...styles.message,
                    backgroundColor: message.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: message.type === 'success' ? '#065f46' : '#991b1b'
                }}>
                    {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                    {message.text}
                    <button onClick={() => setMessage(null)} style={styles.messageClose}>×</button>
                </div>
            )}

            <div style={styles.stats}>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{staffList.length}</div>
                    <div style={styles.statLabel}>현재 직원</div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statValue}>{pendingInvitations.length}</div>
                    <div style={styles.statLabel}>대기중 초대</div>
                </div>
            </div>

            <div style={styles.tabs}>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'staff' ? styles.activeTab : {}) }}
                    onClick={() => setActiveTab('staff')}
                >
                    <Users size={18} />
                    직원 목록 ({staffList.length})
                </button>
                <button
                    style={{ ...styles.tab, ...(activeTab === 'invites' ? styles.activeTab : {}) }}
                    onClick={() => setActiveTab('invites')}
                >
                    <Mail size={18} />
                    초대 관리 ({pendingInvitations.length})
                </button>
                <button onClick={loadData} style={styles.refreshBtn}>
                    <RefreshCw size={18} />
                </button>
            </div>

            {activeTab === 'staff' && (
                <div style={styles.content}>
                    {staffList.length === 0 ? (
                        <div style={styles.empty}>
                            <Users size={48} color="#9ca3af" />
                            <p>등록된 직원이 없습니다</p>
                            <p style={styles.emptyHint}>초대 코드를 생성하여 직원을 초대하세요</p>
                        </div>
                    ) : (
                        <div style={styles.list}>
                            {staffList.map(staff => (
                                <div key={staff.id} style={styles.card}>
                                    <div style={styles.cardHeader}>
                                        <div style={styles.avatar}>
                                            {(staff.name || staff.email || '?')[0].toUpperCase()}
                                        </div>
                                        <div style={styles.cardInfo}>
                                            <h3 style={styles.cardName}>{staff.name || '이름 없음'}</h3>
                                            <p style={styles.cardEmail}>{staff.email}</p>
                                            {staff.phone && <p style={styles.cardPhone}>{staff.phone}</p>}
                                            <p style={styles.cardDate}>가입일: {formatDate(staff.createdAt)}</p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <button
                                            onClick={() => revokeSessions(staff.id, staff.name || staff.email)}
                                            disabled={revoking === staff.id}
                                            style={{ ...styles.revokeBtn, ...(revoking === staff.id ? { opacity: 0.6 } : {}) }}
                                            title="폰·브라우저 포함 모든 기기에서 로그인을 끊습니다 (계정·소속은 유지)"
                                        >
                                            <Smartphone size={16} />
                                            {revoking === staff.id ? '차단 중…' : '모든 기기 로그아웃'}
                                        </button>
                                        <button
                                            onClick={() => removeStaff(staff.id, staff.name || staff.email)}
                                            style={styles.removeBtn}
                                        >
                                            <UserMinus size={16} />
                                            해제
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'invites' && (
                <div style={styles.content}>
                    {/* T-18 — 서버 상수와 같은 문자열, 초대 폼 상단 고정 */}
                    {roadmapOn && (
                        <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.6 }}>{STAFF_INVITE_NOTICE}</div>
                    )}
                    <button onClick={createInvitation} style={styles.createBtn}>
                        <UserPlus size={20} />
                        새 초대 코드 생성
                    </button>

                    {pendingInvitations.length > 0 && (
                        <>
                            <h3 style={styles.sectionTitle}>대기중인 초대</h3>
                            <div style={styles.list}>
                                {pendingInvitations.map(inv => {
                                    const expired = isExpired(inv.expiresAt);
                                    return (
                                        <div key={inv.id} style={{
                                            ...styles.inviteCard,
                                            opacity: expired ? 0.6 : 1
                                        }}>
                                            <div style={styles.inviteHeader}>
                                                <div style={{
                                                    ...styles.inviteStatus,
                                                    backgroundColor: expired ? '#fee2e2' : '#fef3c7',
                                                    color: expired ? '#991b1b' : '#92400e'
                                                }}>
                                                    {expired ? <AlertCircle size={16} /> : <Clock size={16} />}
                                                    {expired ? '만료됨' : '대기중'}
                                                </div>
                                                <span style={styles.inviteDate}>
                                                    생성: {formatDate(inv.createdAt)}
                                                </span>
                                            </div>
                                            {!expired && (
                                                <>
                                                    <div style={styles.codeBox}>
                                                        <span style={styles.code}>{inv.inviteCode}</span>
                                                        <button
                                                            onClick={() => copyToClipboard(inv.inviteCode)}
                                                            style={styles.copyBtn}
                                                        >
                                                            <Copy size={16} />
                                                        </button>
                                                    </div>
                                                    <p style={styles.expireDate}>
                                                        만료: {formatDate(inv.expiresAt)}
                                                    </p>
                                                </>
                                            )}
                                            <button
                                                onClick={() => deleteInvitation(inv.id)}
                                                style={styles.deleteBtn}
                                            >
                                                <Trash2 size={14} />
                                                삭제
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {usedInvitations.length > 0 && (
                        <>
                            <h3 style={styles.sectionTitle}>사용된 초대</h3>
                            <div style={styles.list}>
                                {usedInvitations.map(inv => (
                                    <div key={inv.id} style={styles.usedInviteCard}>
                                        <div style={styles.inviteHeader}>
                                            <div style={styles.usedStatus}>
                                                <CheckCircle size={16} />
                                                사용됨
                                            </div>
                                            <span style={styles.inviteDate}>
                                                {formatDate(inv.usedAt || inv.createdAt)}
                                            </span>
                                        </div>
                                        <p style={styles.usedCode}>{inv.inviteCode}</p>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    {invitations.length === 0 && (
                        <div style={styles.empty}>
                            <Mail size={48} color="#9ca3af" />
                            <p>초대 내역이 없습니다</p>
                            <p style={styles.emptyHint}>위 버튼을 클릭하여 초대 코드를 생성하세요</p>
                        </div>
                    )}

                    <div style={styles.helpBox}>
                        <h4>초대 코드 사용 방법</h4>
                        <ol>
                            <li>초대 코드를 생성합니다</li>
                            <li>코드를 복사하여 직원에게 전달합니다</li>
                            <li>직원이 회원가입 시 "개인 회원" → "직원으로 가입하기" 선택</li>
                            <li>초대 코드를 입력하면 자동으로 직원으로 등록됩니다</li>
                        </ol>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '24px',
    },
    header: {
        marginBottom: '24px',
    },
    title: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '24px',
        fontWeight: '700',
        color: '#1f2937',
        margin: 0,
    },
    subtitle: {
        color: '#6b7280',
        marginTop: '8px',
    },
    loading: {
        textAlign: 'center',
        padding: '48px',
        color: '#6b7280',
    },
    message: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        borderRadius: '8px',
        marginBottom: '16px',
    },
    messageClose: {
        marginLeft: 'auto',
        background: 'none',
        border: 'none',
        fontSize: '20px',
        cursor: 'pointer',
        opacity: 0.7,
    },
    stats: {
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
    },
    statCard: {
        flex: 1,
        backgroundColor: '#f8fafc',
        padding: '20px',
        borderRadius: '12px',
        textAlign: 'center',
    },
    statValue: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#6366f1',
    },
    statLabel: {
        fontSize: '14px',
        color: '#6b7280',
        marginTop: '4px',
    },
    tabs: {
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid #e5e7eb',
        paddingBottom: '8px',
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        border: 'none',
        background: 'none',
        fontSize: '14px',
        fontWeight: '500',
        color: '#6b7280',
        cursor: 'pointer',
        borderRadius: '8px',
    },
    activeTab: {
        backgroundColor: '#eef2ff',
        color: '#6366f1',
    },
    refreshBtn: {
        marginLeft: 'auto',
        padding: '10px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        color: '#6b7280',
        borderRadius: '8px',
    },
    content: {
        minHeight: '300px',
    },
    empty: {
        textAlign: 'center',
        padding: '48px',
        color: '#6b7280',
    },
    emptyHint: {
        fontSize: '14px',
        color: '#9ca3af',
        marginTop: '8px',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    card: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    avatar: {
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        backgroundColor: '#eef2ff',
        color: '#6366f1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px',
        fontWeight: '700',
    },
    cardInfo: {
        flex: 1,
    },
    cardName: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
    },
    cardEmail: {
        margin: '4px 0 0',
        fontSize: '13px',
        color: '#6b7280',
    },
    cardPhone: {
        margin: '2px 0 0',
        fontSize: '12px',
        color: '#9ca3af',
    },
    cardDate: {
        margin: '4px 0 0',
        fontSize: '12px',
        color: '#9ca3af',
    },
    removeBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        backgroundColor: '#fee2e2',
        color: '#dc2626',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    revokeBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        backgroundColor: '#fff7ed',
        color: '#b45309',
        border: '1px solid #fed7aa',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '500',
        cursor: 'pointer',
    },
    createBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        width: '100%',
        padding: '14px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        marginBottom: '24px',
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '12px',
        marginTop: '24px',
    },
    inviteCard: {
        padding: '16px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
    },
    inviteHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
    },
    inviteStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
    },
    inviteDate: {
        fontSize: '12px',
        color: '#9ca3af',
    },
    codeBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        marginBottom: '8px',
    },
    code: {
        fontSize: '20px',
        fontWeight: '700',
        fontFamily: 'monospace',
        letterSpacing: '2px',
        color: '#1f2937',
    },
    copyBtn: {
        padding: '8px',
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        cursor: 'pointer',
        color: '#6b7280',
    },
    expireDate: {
        fontSize: '12px',
        color: '#6b7280',
        margin: 0,
    },
    deleteBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginTop: '12px',
        padding: '6px 10px',
        backgroundColor: 'transparent',
        color: '#dc2626',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        fontSize: '12px',
        cursor: 'pointer',
    },
    usedInviteCard: {
        padding: '12px 16px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
    },
    usedStatus: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        backgroundColor: '#d1fae5',
        color: '#065f46',
        borderRadius: '6px',
        fontSize: '13px',
        fontWeight: '500',
    },
    usedCode: {
        fontSize: '14px',
        fontFamily: 'monospace',
        color: '#9ca3af',
        margin: '8px 0 0',
    },
    helpBox: {
        marginTop: '32px',
        padding: '20px',
        backgroundColor: '#f0f9ff',
        borderRadius: '12px',
        border: '1px solid #bae6fd',
    },
};

export default StaffInviteManagement;
